/**
 * Calendar — shadcn 표준 패턴의 react-day-picker v10 래퍼.
 *
 * 사용처: DatePicker (Popover + Calendar 조합). EventCreateModal 시작/종료
 * 날짜 입력에서 native datetime-local 대신 사용.
 *
 * 디자인 토큰 정합: rounded-control / text-label / bg-primary / border-border 등
 * NEXTPOD 의 시맨틱 토큰을 그대로 사용 — 인라인 dark: 없음.
 */
import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { ko } from "date-fns/locale";

import { cn } from "../../lib/utils";
import { buttonVariants } from "./button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      locale={ko}
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row gap-4",
        month: "flex flex-col gap-3",
        month_caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-label font-semibold text-foreground",
        nav: "flex items-center gap-1",
        button_previous: cn(
          buttonVariants({ variant: "ghost", size: "icon" }),
          "size-7 bg-transparent text-muted-foreground hover:text-foreground absolute left-1 top-1"
        ),
        button_next: cn(
          buttonVariants({ variant: "ghost", size: "icon" }),
          "size-7 bg-transparent text-muted-foreground hover:text-foreground absolute right-1 top-1"
        ),
        month_grid: "w-full border-collapse",
        weekdays: "flex",
        weekday: "text-muted-foreground rounded-md w-8 font-normal text-meta",
        week: "flex w-full mt-1",
        day: cn(
          "relative p-0 text-center text-label",
          "[&:has([aria-selected])]:bg-primary/10",
          "first:[&:has([aria-selected])]:rounded-l-control",
          "last:[&:has([aria-selected])]:rounded-r-control"
        ),
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "size-8 p-0 font-normal aria-selected:opacity-100 hover:bg-muted cursor-pointer"
        ),
        range_start: "day-range-start aria-selected:bg-primary aria-selected:text-primary-foreground",
        range_end: "day-range-end aria-selected:bg-primary aria-selected:text-primary-foreground",
        selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        today: "bg-primary/15 text-primary font-semibold",
        outside: "text-muted-foreground/40 aria-selected:bg-primary/10",
        disabled: "text-muted-foreground/40 opacity-50",
        range_middle: "aria-selected:bg-primary/10 aria-selected:text-foreground",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, ...iconProps }) => {
          const Icon = orientation === "left" ? ChevronLeft : ChevronRight;
          return <Icon className="size-4" {...iconProps} />;
        },
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
